// CltvEchart.js
import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useTheme } from '../contexts/ThemeContext';
import { color } from 'framer-motion';


// Helper to create line chart options
const createLineChartOption = (axisColor, title, xData, seriesData, yAxisLabel) => ({
    title: {
        text: title,
        left: 'center',
        textStyle: { fontSize: 18, color: axisColor }
    },
    tooltip: {
        trigger: 'axis'
    },
    legend: {
        top: 30,
        textStyle: { fontSize: 12, color: axisColor }
    },
    xAxis: {
        type: 'category',
        data: xData,
        axisLabel: {
            rotate: 0,
            color: axisColor
        }
    },
    yAxis: {
        type: 'value',
        name: yAxisLabel,
        axisLabel: {
            color: axisColor
        },
        nameTextStyle: { color: axisColor },
    },
    series: seriesData
});

// Helper to create bar chart options
const createBarChartOption = (axisColor, title, xData, yData, colorList, yAxisLabel, textLabels) => ({
    title: {
        text: title,
        left: 'center',
        textStyle: { fontSize: 18, color: axisColor }
    },
    tooltip: {
        trigger: 'axis',
        axisPointer: {
            type: 'shadow'
        }
    },
    xAxis: {
        type: 'category',
        data: xData,
        axisLabel: {
            rotate: 45,
            color: axisColor
        }
    },
    yAxis: {
        type: 'value',
        name: yAxisLabel,
        axisLabel: {
            color: axisColor
        }
    },
    series: [{
        type: 'bar',
        data: yData.map((y, i) => ({ value: y, itemStyle: { color: colorList[i] }, label: { show: true, position: 'top', formatter: textLabels[i] } })),
        label: {
            show: true,
            position: 'top',
            textStyle: {
                color: axisColor
            }
        }
    }]
});



export const ECltvChartBgnbd = ({ data }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const axisColor = isDark ? '#ccc' : '#333';

    const option = createLineChartOption(axisColor,
        'Decile Plot - Actual vs Predicted Frequency',
        data.map(d => d.decile),
        [
            {
                name: 'Average Actual Frequency',
                type: 'line',
                data: data.map(d => d.avg_actual_frequency),
                symbol: 'circle'
            },
            {
                name: 'Average Predicted Frequency',
                type: 'line',
                data: data.map(d => d.avg_predicted_frequency),
                symbol: 'x'
            }
        ],
        'Average Frequency in Holdout Period'
    );
    return <ReactECharts option={option} style={{ height: 500 }} />;
};

export const ECltvChartGgf = ({ data }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const axisColor = isDark ? '#ccc' : '#333';

    const option = createLineChartOption(axisColor,
        'Decile Plot - Avg Occasion Value Prediction',
        data.map(d => d.decile),
        [
            {
                name: 'Actual Avg Occasion Value',
                type: 'line',
                data: data.map(d => d.avg_actual),
                symbol: 'circle'
            },
            {
                name: 'Predicted Avg Occasion Value',
                type: 'line',
                data: data.map(d => d.avg_predicted),
                symbol: 'x'
            }
        ],
        'Average Monetary Value per Occasion'
    );
    return <ReactECharts option={option} style={{ height: 500 }} />;
};

export const ECltvChartSpend = ({ data }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const axisColor = isDark ? '#ccc' : '#333';

    const option = createLineChartOption(axisColor,
        'Decile Plot - Predicted CLTV vs Actual Holdout Spend',
        data.map(d => d.decile),
        [
            {
                name: 'Avg Actual Holdout Spend',
                type: 'line',
                data: data.map(d => d.avg_actual_spend),
                symbol: 'circle'
            },
            {
                name: 'Avg Predicted CLTV',
                type: 'line',
                data: data.map(d => d.avg_predicted_cltv),
                symbol: 'x'
            }
        ],
        'Average Total Spend in Holdout'
    );
    return <ReactECharts option={option} style={{ height: 500 }} />;
};

export const ECltvChartSegment = ({ data }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const axisColor = isDark ? '#ccc' : '#333';

    const clvDecileGroups = data.map(d => `Decile ${d.clv_decile_group}`);
    const avgCltv = data.map(d => d.avg_predicted_cltv);
    const customerLabels = data.map(d => `n=${d.number_of_customers}`);

    // gradient: light blue to peach to dark red
    const gradientColors = ['#AEC8FF', '#F2C090', '#BF5500'];
    const interpolateColor = (c1, c2, f) => {
        const hex = h => parseInt(h.replace('#', ''), 16);
        const rgb = hex(c1), rgb2 = hex(c2);
        const r1 = (rgb >> 16) & 255, g1 = (rgb >> 8) & 255, b1 = rgb & 255;
        const r2 = (rgb2 >> 16) & 255, g2 = (rgb2 >> 8) & 255, b2 = rgb2 & 255;
        const r = Math.round(r1 + f * (r2 - r1)), g = Math.round(g1 + f * (g2 - g1)), b = Math.round(b1 + f * (b2 - b1));
        return `rgb(${r},${g},${b})`;
    };
    const colorList = avgCltv.map((_, i) => {
        const f = i / (avgCltv.length - 1);
        return f < 0.5
            ? interpolateColor(gradientColors[0], gradientColors[1], f / 0.5)
            : interpolateColor(gradientColors[1], gradientColors[2], (f - 0.5) / 0.5);
    });

    const option = createBarChartOption(axisColor,
        'Average Predicted CLTV per Decile',
        clvDecileGroups,
        avgCltv,
        colorList,
        'Average Predicted CLTV',
        customerLabels
    );
    return <ReactECharts option={option} style={{ height: 600 }} />;
};
